/**
 * WorkflowSettingsTab - Aba "Workflow" das Configurações do tenant
 *
 * Concentra o interruptor do módulo Workflow (esteira comercial parametrizável),
 * antes disponível apenas dentro da própria tela do módulo.
 *
 * Enquanto a flag `tenants.workflow_enabled` estiver desligada, TODA a estrutura
 * do Workflow fica inerte: menu escondido, rotas bloqueadas pelo guard, trilha do
 * negócio oculta, gatilho de fluxo indisponível e nenhum evento gravado no banco.
 *
 * @version 1.0.0
 * RELEASE: 2026-08-29 v1.0.0 - Criação inicial (varredura das Fases 1..5)
 */

import { Info, Route } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import {
  SettingsCard,
  SettingsInfoBanner,
  SettingsSectionHeader,
} from '@/components/tenant/SettingsInfoRow';
import { useWorkflowModule } from '@/hooks/useWorkflowModule';

/** Aba de configurações do módulo Workflow */
export function WorkflowSettingsTab() {
  const { isEnabled, isLoading, toggleModule } = useWorkflowModule();

  if (isLoading) {
    return <Skeleton className="h-56 w-full rounded-lg" />;
  }

  return (
    <div className="space-y-6">
      <SettingsCard>
        <SettingsSectionHeader
          eyebrow="Módulo"
          title="Workflow"
          description="Esteira comercial parametrizável: define a sequência do processo de venda (negócio, proposta, contrato e cobrança) e o que o sistema faz em cada etapa."
        />

        {/* ---------------- Interruptor do módulo ---------------- */}
        <div className="flex items-center justify-between gap-4 px-6 py-4">
          <div className="flex items-start gap-3">
            <Route className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
            <div className="space-y-1 text-sm">
              <p className="font-medium text-foreground">Habilitar Workflow</p>
              <p className="text-muted-foreground">
                Exibe o menu Workflow em Configurações, libera a tela de montagem da esteira, a
                trilha de etapas no negócio e o gatilho "Etapa do Workflow" nos fluxos.
              </p>
            </div>
          </div>
          <Switch
            checked={isEnabled}
            disabled={toggleModule.isPending}
            onCheckedChange={(checked) => toggleModule.mutate(checked)}
          />
        </div>

        <div className="px-6 pb-5">
          <SettingsInfoBanner icon={Info}>
            Habilitar não altera nada sozinho: nenhuma etapa é aplicada e nenhuma regra bloqueia o
            time enquanto não existir uma esteira publicada em Configurações → Workflow. Ao
            desabilitar, tudo volta a ficar oculto e o sistema para de registrar eventos da
            esteira, sem perder o que já foi configurado.
          </SettingsInfoBanner>
        </div>
      </SettingsCard>
    </div>
  );
}
